import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { protectedRoutes } from 'constants/routes'

import { AgGridReact } from 'ag-grid-react'
// import clsx from 'clsx'
import ShowLastUpdateDatetime from 'components/commonparts/ShowLastUpdateDatetime/ShowLastUpdateDatetime'
import { LoadingOverlay } from 'components/commonparts/index'
import { useGridBase } from 'hooks/useGridBase'
import 'utils/mst.scss'

import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import type { OrderAlert, OrderAlertList } from 'api-client'
import type { DataListStatus } from 'types/types'

interface DataListProps {
  list: OrderAlertList
  isLoading: boolean
  listStatus: DataListStatus
  onListStatusChange: (status: DataListStatus) => void
}

type OrderAlertRow = OrderAlert & { id: string }

function OrderAlertDataList({ list, isLoading, listStatus, onListStatusChange }: DataListProps) {
  const gridRef = useRef<AgGridReact<OrderAlertRow>>(null)
  const [loadingState, setLoadingState] = useState<boolean>(false)
  const lastApiCallRef = useRef<number>(0)
  const containerStyle = useMemo(
    () => ({
      width: '100%',
      height: 'calc(100vh - 500px)',
      minHeight: '280px',
      display: 'flex',
      flexDirection: 'column' as const,
    }),
    [],
  )
  const gridStyle = useMemo(() => ({ width: '100%', flexGrow: 1, minHeight: 0 }), [])

  const {
    onGridReady,
    localeText,
    defaultColDef,
  } = useGridBase({
    gridRef,
    isLoading: isLoading || loadingState,
  })
  const rowData = useMemo<OrderAlertRow[]>(
    () =>
      list.list.map((item, index) => ({
        ...item,
        // Stable key for AG Grid reconciliation, similar to other DataList screens.
        id: `${item.companyId ?? ''}:${item.centerId ?? ''}:${item.orgCenterId ?? ''}:${item.orgSupplierId ?? ''}:${index}`,
      })),
    [list.list]
  )

  const buildStockCenterLink = useCallback((params: ICellRendererParams) => {
    const centerIds = params.data?.centerId != null ? [params.data.centerId] : []
    const orgSupplierIds = params.data?.orgSupplierId != null ? [params.data.orgSupplierId] : []
    const query = new URLSearchParams({
      company_id: String(params.data?.companyId ?? ''),
      center_ids: centerIds.join(','),
      org_supplier_ids: orgSupplierIds.join(','),
      item_codes: '',
    }).toString()
    return `/${protectedRoutes.StockCenter.path}?${query}`
  }, [])

  // Cell renderer for today order count with link
  const TodayOrderCountRenderer = useCallback((params: ICellRendererParams) => {
    if (params.value == null) return null

    return (
      <Link
        className="text-blue-400 underline"
        style={{ textDecoration: 'none' }}
        to={buildStockCenterLink(params)}
        target="_blank"
        rel="noopener noreferrer"
      >
        {params.value}
      </Link>
    )
  }, [buildStockCenterLink])

  const columnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: 'センター',
      field: 'orgCenterId',
      width: 250,
      suppressSizeToFit: true,
      pinned: 'left',
      cellClass: 'string-cell',
      valueGetter: (params) => {
        const orgCenterId = params.data?.orgCenterId
        const centerName = params.data?.centerName
        if (orgCenterId == null && !centerName) return ''
        return `[${orgCenterId ?? ''}]${centerName ?? ''}`
      },
    },
    {
      headerName: '仕入先',
      field: 'orgSupplierId',
      width: 300,
      suppressSizeToFit: true,
      pinned: 'left',
      cellClass: 'string-cell',
      valueGetter: (params) => {
        const orgSupplierId = params.data?.orgSupplierId
        const supplierName = params.data?.supplierName
        if (orgSupplierId == null && !supplierName) return ''
        return `[${orgSupplierId ?? ''}]${supplierName ?? ''}`
      },
    },
    {
      headerName: '本日発注数',
      field: 'todayOrderCount',
      flex: 1,
      suppressSizeToFit: true,
      cellClass: 'number-cell',
      cellRenderer: TodayOrderCountRenderer,
    },
    {
      headerName: '在庫引当アラート',
      field: 'stockAlertCount',
      flex: 1,
      cellClass: 'number-cell',
    },
    {
      headerName: '欠品アラート',
      field: 'missingItemAlertCount',
      flex: 1,
      suppressSizeToFit: true,
      cellClass: 'number-cell',
    },
    {
      headerName: '在庫量低下アラート',
      field: 'stockDaysAlertCount',
      flex: 1,
      suppressSizeToFit: true,
      cellClass: 'number-cell',
    },
    {
      headerName: '出荷予測精度低',
      field: 'expectedRankAlertCount',
      flex: 1,
      suppressSizeToFit: true,
      cellClass: 'number-cell',
    },
    {
      headerName: '新商品',
      field: 'minShipDateAlertCount',
      flex: 1,
      suppressSizeToFit: true,
      cellClass: 'number-cell',
    },
    {
      headerName: '終売',
      field: 'endSaleAlertCount',
      flex: 1,
      suppressSizeToFit: true,
      cellClass: 'number-cell',
    },
    {
      headerName: 'マスタ未登録',
      field: 'masterNotFoundAlertCount',
      flex: 1,
      suppressSizeToFit: true,
      cellClass: 'number-cell',
    },
    {
      headerName: 'その他マスタアラート',
      field: 'otherItemAlertCount',
      flex: 1,
      suppressSizeToFit: true,
      cellClass: 'number-cell',
    },
  ], [TodayOrderCountRenderer])

  // Handle pagination changes with debouncing to prevent excessive API calls
  const onPaginationChanged = useCallback(() => {
    const now = Date.now()
    if (!gridRef.current?.api) return
    
    const currentPage = gridRef.current.api.paginationGetCurrentPage()
    const pageSize = gridRef.current.api.paginationGetPageSize()

    // Don't trigger API call during loading to avoid double calls
    if (isLoading || loadingState) return

    // Debounce: only allow one API call per 500ms
    if (now - lastApiCallRef.current < 500) return

    if (currentPage !== listStatus.paginationModel.page || pageSize !== listStatus.paginationModel.pageSize) {
      // Update debounce timestamp
      lastApiCallRef.current = now

      // Set loading state when making API call
      setLoadingState(true)

      // When page size changes, reset to page 0
      const targetPage = pageSize !== listStatus.paginationModel.pageSize ? 0 : currentPage

      onListStatusChange({
        sortModel: listStatus.sortModel,
        paginationModel: { page: targetPage, pageSize }
      })
    }
  }, [listStatus.sortModel, listStatus.paginationModel.page, listStatus.paginationModel.pageSize, onListStatusChange, isLoading, loadingState])

  // Reset loading state when new data arrives
  useEffect(() => {
    if (!isLoading && loadingState) {
      setLoadingState(false)
    }
  }, [isLoading, loadingState])

  // Keep pagination panel hidden while loading to avoid stale page information.
  useEffect(() => {
    const timer = setTimeout(() => {
      const paginationPanel = document.querySelector('.ag-paging-panel')
      if (paginationPanel) {
        ;(paginationPanel as HTMLElement).style.visibility = isLoading || loadingState ? 'hidden' : 'visible'
      }
    }, 10)
    return () => clearTimeout(timer)
  }, [isLoading, loadingState])

  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) return

    if (isLoading || loadingState) {
      api.setGridOption('loading', true)
      return
    }

    if (list.list.length === 0) {
      api.showNoRowsOverlay()
      return
    }

    api.setGridOption('loading', false)
    api.hideOverlay()
  }, [isLoading, loadingState, list.list])

  return (
    <div style={containerStyle}>
      <ShowLastUpdateDatetime<OrderAlert> list={list.list} lastUpdateDatetime={list.lastUpdateDatetime} />
      <div style={gridStyle} className="ag-theme-balham">
        <AgGridReact<OrderAlertRow>
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection={{
            mode: 'singleRow',
            checkboxes: false,
            enableClickSelection: false,
          }}
          onGridReady={onGridReady}
          localeText={localeText}
          loadingOverlayComponent={LoadingOverlay}
          getRowId={(params) => params.data.id}
          pagination
          paginationPageSize={listStatus.paginationModel.pageSize}
          paginationPageSizeSelector={[100, 300, 500]}
          onPaginationChanged={onPaginationChanged}
          suppressPaginationPanel={false}
          suppressScrollOnNewData
          rowHeight={36}
          suppressColumnVirtualisation
          rowModelType="clientSide"
        />
      </div>
    </div>
  )
}

export default OrderAlertDataList
